import { CornerDownLeft, Search } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

import { createTargets, navTargets, ShellItem } from './nav-items'

const normalize = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  features?: string[]
}

/** ⌘K paleta: navigace a rychlé vytvoření dokladů. */
export const CommandPalette = ({ open, onOpenChange, features }: CommandPaletteProps) => {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  const allowed = (i: ShellItem) => !i.feature || !features || features.includes(i.feature)

  const groups = useMemo(() => {
    const q = normalize(query.trim())
    const match = (i: ShellItem) => !q || normalize(`${i.label} ${i.keywords ?? ''}`).includes(q)
    return [
      { title: 'Vytvořit', items: createTargets.filter(allowed).filter(match).map((i) => ({ ...i, label: `Nová ${i.label.toLowerCase()}`.replace('Nová náklad', 'Nový náklad').replace('Nová kontakt', 'Nový kontakt').replace('Nová produkt', 'Nový produkt') })) },
      { title: 'Přejít na', items: navTargets.filter(allowed).filter(match) },
    ].filter((g) => g.items.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, features])

  const flat = groups.flatMap((g) => g.items)

  useEffect(() => {
    setIndex(0)
  }, [query])

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const run = (item: ShellItem | undefined) => {
    if (!item) return
    onOpenChange(false)
    navigate(item.href)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setIndex((i) => Math.min(i + 1, flat.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      run(flat[index])
    }
  }

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [index])

  let cursor = -1

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="top-[18%] translate-y-0 gap-0 overflow-hidden rounded-xl p-0 sm:max-w-[560px]">
        <DialogTitle className="sr-only">Hledat a přejít</DialogTitle>
        <div className="flex items-center gap-2 border-b px-4">
          <Search className="size-4 text-muted-foreground" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Hledat stránku nebo akci..."
            className="h-12 w-full bg-transparent text-[15px] outline-none placeholder:text-muted-foreground"
          />
          <kbd className="rounded border px-1.5 text-[11px] text-muted-foreground">Esc</kbd>
        </div>
        <div ref={listRef} className="max-h-[360px] overflow-y-auto p-1.5">
          {flat.length === 0 && <p className="px-3 py-8 text-center text-sm text-muted-foreground">Nic nenalezeno.</p>}
          {groups.map((g) => (
            <div key={g.title} className="py-1">
              <p className="px-2.5 pb-1 pt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{g.title}</p>
              {g.items.map((item) => {
                cursor += 1
                const active = cursor === index
                const myIndex = cursor
                return (
                  <button
                    key={`${g.title}-${item.id}`}
                    type="button"
                    data-active={active}
                    onMouseMove={() => setIndex(myIndex)}
                    onClick={() => run(item)}
                    className={cn(
                      'flex h-9 w-full items-center gap-3 rounded-md px-2.5 text-left text-sm',
                      active ? 'bg-muted text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    <item.icon className="size-4" />
                    <span className="flex-1 text-foreground">{item.label}</span>
                    {active && <CornerDownLeft className="size-3.5 text-muted-foreground" />}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
